import { typing } from './useVisualViewport';

/** The app's root and the two layers of the shell under it, none of which ever scrolls. */
const SHELL_BOXES = '#root, #root > *, #root > * > *';

/** When to look again after the focus moves: an iPhone settles late, and in steps. */
const SETTLE_MS = [0, 60, 250, 500, 900];

/**
 * Puts the page back after the iPhone keyboard. Request of 6 October 2026.
 *
 * The document itself never scrolls: the shell is one screen tall and each
 * page scrolls inside it. An iPhone pans the document up all the same, to bring
 * a field above the keyboard, and does not always pan it back when the keyboard
 * goes. What was left was the whole app shifted up: the top bar off the screen,
 * and under the page an empty strip that took no taps, as tall as the keyboard
 * had been.
 *
 * A finger cannot undo it, because the document and the shell hide their
 * overflow, which is why only reloading the page did.
 *
 * So whenever nothing is being typed into and the document is found scrolled,
 * it is put back to the top. While a field has the focus it is left alone: the
 * pan is what keeps that field in sight.
 */
export function keepDocumentStill(): () => void {
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const settleNow = () => {
    if (typing()) return;
    const root = document.documentElement;
    if (window.scrollY !== 0 || window.scrollX !== 0 || root.scrollTop !== 0 || document.body.scrollTop !== 0) {
      window.scrollTo(0, 0);
      root.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    /*
     * The shell's own boxes too. They hide their overflow, so a finger cannot
     * scroll them, but bringing a field into view can, and then nothing but a
     * reload brought them back.
     */
    for (const box of document.querySelectorAll<HTMLElement>(SHELL_BOXES)) {
      if (box.scrollTop !== 0 && getComputedStyle(box).overflowY === 'hidden') box.scrollTop = 0;
    }
  };

  const settle = () => {
    for (const delay of SETTLE_MS) {
      const timer = setTimeout(() => {
        timers.delete(timer);
        settleNow();
      }, delay);
      timers.add(timer);
    }
  };

  const viewport = window.visualViewport;
  document.addEventListener('focusout', settle);
  window.addEventListener('scroll', settleNow, { passive: true });
  window.addEventListener('orientationchange', settle);
  window.addEventListener('pageshow', settle);
  viewport?.addEventListener('resize', settle);

  return () => {
    document.removeEventListener('focusout', settle);
    window.removeEventListener('scroll', settleNow);
    window.removeEventListener('orientationchange', settle);
    window.removeEventListener('pageshow', settle);
    viewport?.removeEventListener('resize', settle);
    timers.forEach((timer) => {
      clearTimeout(timer);
    });
  };
}
